import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';

import React, { useMemo, useState } from 'react';

import { RouteComponentProps, withRouter } from 'react-router-dom';

import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import '../Data.scss';
import './MainDataPage.scss';

import { Button, Card, Modal } from 'semantic-ui-react';
import { CommonDataFields, useDataCategories, useThemeDatasets } from '../../../shared/api/apiHooks';
import {
  DatasetPathCategoryParams,
  getBrowseDataFieldsPath,
  getDatasetsPath,
  getDatasetsPathWithCategory,
  getSingleDatasetPath,
} from '../dataExplorerRouterPaths';
import { CategoriesModal } from '../Categories/CategoriesModal';
import { useDataCategoryField } from '../Filters/FilterFields/CategoryField';
import { MainDataSearch } from './MainDataSearch/MainDataSearch';
import {
  CommonDataFieldsSelector,
  commonFieldTransformers,
  getDefaultCommonDataFilterValues,
} from '../CommonDataFieldsSelector/CommonDataFieldsSelector';
import { useFilterForm } from '../Filters/useFilterForm';
import { useWatch } from 'react-hook-form';
import { GenericFieldTransformer } from '../Filters/FieldTransformers/FieldTransformers';
import { addModifiers } from '../../../shared/helpers';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { stringifyFilters } from '../Filters/queryTransformers';
import { DataExplorerLink, useDataExplorerHistory } from '../dataExplorerHistoryContext';
import { DataCategory, Region, TopDataset } from '../../../shared/api/api';
import { getUserPermissionSet, hasConsultantOrResearcherPermission } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import { useSelector } from 'react-redux';
import { ThemeLabel } from '../DataTableCommon/ThemeLabel';
import { PyramidMultipliersTable } from '../../../components/Alphas/AlphaPyramids/AlphaPyramidMultipliers';

const SEARCH_FIELD_NAME = filterFields.search.name;

const fieldTransformers = [new GenericFieldTransformer(SEARCH_FIELD_NAME), ...commonFieldTransformers];
const searchDefaultValues = {
  [SEARCH_FIELD_NAME]: '',
};

function MainDataPage({ intl }: InjectedIntlProps & RouteComponentProps) {
  const history = useDataExplorerHistory();
  const permissions = useSelector(getUserPermissionSet);
  const isConsultant = permissions.has('CONSULTANT');
  const isResearcher = permissions.has('RESEARCHER');

  const onCategoryClick = (params: DatasetPathCategoryParams) => {
    history.push(getDatasetsPathWithCategory(params));
  };

  const category = useDataCategoryField(onCategoryClick);

  const defaultValues = useMemo(() => ({ ...searchDefaultValues, ...getDefaultCommonDataFilterValues() }), []);

  const { control, debouncedQueryObject, flushDebounce, reset } = useFilterForm({
    fieldTransformers,
    defaultValues,
    debounce: 500,
  });

  const values = useWatch({ control }) as CommonDataFields;
  const trackEvent = useDataExplorerTrackCustomEvent();

  return (
    <div className="data-main data-main__main-page">
      <div className="data-main__common-parameters-row">
        <div className={addModifiers('data__header-container', ['main-search'])}>
          <CommonDataFieldsSelector classModifier={'inverse'} control={control} />
        </div>
      </div>
      <div className="data__hero-section">
        <h1 className="data__header-title">
          <FormattedMessageHelper id="dataExplorer.searchHeader" />
        </h1>

        <MainDataSearch
          control={control}
          values={values}
          reset={reset}
          flushDebounce={flushDebounce}
          debouncedQueryObject={debouncedQueryObject}
        />

        <div className="data-main__browse-buttons-section">
          <DataExplorerLink to={getDatasetsPath()}>
            <Button onClick={() => trackEvent('de_click_browse_all_datasets')} className="button--primary">
              <FormattedMessageHelper id={'dataExplorer.main.browseDatasets'} />
            </Button>
          </DataExplorerLink>

          <DataExplorerLink to={getBrowseDataFieldsPath()}>
            <Button onClick={() => trackEvent('de_click_browse_all_fields')} className="button--primary">
              <FormattedMessageHelper id={'dataExplorer.main.browseFields'} />
            </Button>
          </DataExplorerLink>

          <Button
            onClick={() => {
              trackEvent('de_click_categories_modal_open');
              category.toggleCategoriesOpen();
            }}
            className="data-main__category-button"
          >
            <FormattedMessageHelper id={'dataExplorer.main.browseCategories'} />
          </Button>
        </div>
      </div>
      {(isConsultant || isResearcher) && (
        <ThemeDatasets universe={values.universe} instrumentType={values.instrumentType} delay={values.delay} region={values.region} />
      )}
      <CategoryCards selectedDelay={values.delay} selectedUniverse={values.universe} selectedRegion={values.region} intl={intl} />

      {category.isCategoriesOpen && (
        <CategoriesModal
          control={control}
          onCategoryClick={category.onCategoryClick}
          onClose={() => category.toggleCategoriesOpen(false)}
        />
      )}
    </div>
  );
}

type MainPageDatasetType = 'theme' | 'starter' | 'newest';

function ThemeDatasets({ region, instrumentType, delay, universe }: CommonDataFields) {
  const { data, isError, isFetching } = useThemeDatasets({ region, delay, universe, instrumentType });
  const trackEvent = useDataExplorerTrackCustomEvent();

  if (isError) {
    return <></>;
  }
  if (data && data.results?.length === 0) {
    return <></>;
  }

  const results = !isFetching && data?.results ? data?.results : ([{}, {}, {}, {}] as TopDataset[]);

  const onClickBrowseAllThemes = () => trackEvent('de_click_browse_all_datasets');
  return (
    <div className="data-main__section  data-main__section--theme">
      <h2 className="data-main__subtitle">
        <FormattedMessageHelper id={'dataExplorer.main.theme'} />
      </h2>

      <div className="data-main__dataset-list">
        {results.map((dataset, i) => (
          <DatasetCard
            type={'theme'}
            key={dataset.id ?? i}
            dataset={dataset}
            selectedUniverse={universe}
            selectedRegion={region}
            selectedDelay={delay}
          />
        ))}
      </div>
      <div className="data-main__browse-themes-button-container">
        <DataExplorerLink to={getDatasetsPath({ queryParams: { theme: 'true' } })}>
          <button onClick={onClickBrowseAllThemes} className="data-main__browse-themes-button button button--secondary">
            <FormattedMessageHelper id={'dataExplorer.main.themeDatasets'} />
          </button>
        </DataExplorerLink>
      </div>
    </div>
  );
}

interface CategoryCardsProps extends InjectedIntlProps {
  selectedDelay: string;
  selectedUniverse: string;
  selectedRegion: string;
}

function CategoryCards({ selectedDelay, selectedUniverse, selectedRegion, intl }: CategoryCardsProps) {
  const { data, isError, isFetching } = useDataCategories();
  const isPyramidMultiplierVisible = useSelector(hasConsultantOrResearcherPermission);
  // Add state to manage the visibility of the pyramid multipliers popup
  const [isPyramidPopupOpen, setIsPyramidPopupOpen] = useState(false);
  if (isError) {
    return <></>;
  }
  const categories = !isFetching && data ? data : (Array.from({ length: 16 }, v => ({})) as DataCategory[]);

  // Function to handle the opening of the pyramid multipliers popup
  function onClickPyramidMultipliers() {
    setIsPyramidPopupOpen(true);
  }

  // Function to handle the closing of the pyramid multipliers popup
  function handleClosePyramidPopup() {
    setIsPyramidPopupOpen(false);
  }

  return (
    <div className="data-main__section  data-main__section--categories">
      <div className="data-main__section-header">
        {/* Empty div to align the header: */}
        <div className="data-main__section-header-placeholder"></div>

        <h2 className="data-main__subtitle">
          <FormattedMessageHelper id={'dataExplorer.main.categoriesHeader'} />
        </h2>
        <div className="data-main__section-header-placeholder">
          {isPyramidMultiplierVisible && (
            <button onClick={onClickPyramidMultipliers} className="data-main__pyramid-multiplier_button button button--secondary">
              <FormattedMessageHelper id={'dataExplorer.main.seeAllMultipliers'} />
            </button>
          )}
        </div>
      </div>

      <div className="data-main__dataset-list">
        {categories?.map(category => (
          <CategoryCard
            type={'starter'}
            key={category.id}
            category={category}
            selectedDelay={selectedDelay}
            selectedRegion={selectedRegion}
            selectedUniverse={selectedUniverse}
          />
        ))}
      </div>
      {/* Pyramid multipliers popup */}
      {isPyramidPopupOpen && (
        <Modal
          open={true}
          size="fullscreen"
          onClose={handleClosePyramidPopup}
          className="data-main__pyramid-modal"
          dimmer={{ className: 'data-main__pyramid-modal-dimmer' }}
        >
          <Modal.Content>
            <div className="pyramid-multipliers__content">
              <PyramidMultipliersTable intl={intl} openInSameTab={true} />
            </div>
            <div className="pyramid-multipliers__buttons">
              <button className="button pyramid-multipliers__button" onClick={handleClosePyramidPopup}>
                <FormattedMessageHelper id="agreements.close" />
              </button>
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}

interface CategoryCardProps {
  category: DataCategory;
  type: MainPageDatasetType;
  selectedDelay: string;
  selectedUniverse: string;
  selectedRegion: string;
}

interface ExisitingUniverseParams {
  existingRegions?: Region[];
  selectedRegion: string;
  selectedDelay: string;
  selectedUniverse: string;
}

function getPopularRegion(existingRegions?: Region[]) {
  if (!existingRegions) {
    return undefined;
  }
  ['USA', 'GLB', 'EUR'].forEach(region => {
    if (existingRegions.includes(region as Region)) {
      return region;
    }
  });
  return existingRegions?.[0];
}

function getExistingUniverse({ existingRegions, selectedRegion, selectedDelay, selectedUniverse }: ExisitingUniverseParams) {
  const isRegionInCategory = existingRegions?.includes(selectedRegion as Region) ?? false;
  const region = isRegionInCategory ? selectedRegion : getPopularRegion(existingRegions) ?? filterFields.region.defaultValue;
  const delay = isRegionInCategory ? selectedDelay : filterFields.delay.defaultValue;
  const universe = isRegionInCategory ? selectedUniverse : '';
  return { region, delay, universe };
}

function CategoryCard({ category, type, selectedUniverse, selectedRegion, selectedDelay }: CategoryCardProps) {
  const history = useDataExplorerHistory();
  const trackEvent = useDataExplorerTrackCustomEvent();
  const cardClickHandler = () => {
    const categoryId = category.id;
    if (!categoryId) {
      return;
    }
    const { region, delay, universe } = getExistingUniverse({
      existingRegions: category?.region as Region[],
      selectedRegion: selectedRegion,
      selectedDelay: selectedDelay,
      selectedUniverse: selectedUniverse,
    });

    const datasetsPathWithCategory = getDatasetsPath({
      queryParams: { category: categoryId, region, delay, universe },
    });
    history.push(datasetsPathWithCategory);
    trackEvent(`de_click_category`, { event_label: categoryId });
  };

  return (
    <Card className="dataset-card" onClick={cardClickHandler}>
      <Card.Content>
        <h3 className="dataset-card__header">{category?.name ?? <Skeleton />}</h3>

        <Card.Description className="dataset-card__description">
          {category?.datasetCount ? (
            <>
              <div>
                <strong>
                  <FormattedMessageHelper id={'dataExplorer.filters.valueScoreLabel'} />
                </strong>
                : {category.valueScore}
              </div>
              <br />
              <div>
                <strong>
                  <FormattedMessageHelper id={'dataExplorer.filters.datasetsLabel'} />
                </strong>
                : {category.datasetCount}
              </div>
              <div>
                <strong>
                  <FormattedMessageHelper id={'dataExplorer.filters.fieldsLabel'} />
                </strong>
                : {category.fieldCount}
              </div>
            </>
          ) : (
            <>
              <Skeleton count={1} />
              <br />
              <Skeleton count={2} />
            </>
          )}
        </Card.Description>
      </Card.Content>

      <Card.Content extra>
        {type === 'starter' && (
          <div className="dataset-card__regions">
            {category?.region ? (
              <>
                <FormattedMessageHelper id={'dataExplorer.main.regions'} /> {category?.region?.join(', ')}
              </>
            ) : (
              <Skeleton />
            )}
          </div>
        )}
      </Card.Content>
    </Card>
  );
}

interface DatasetCartProps {
  dataset: TopDataset;
  type: MainPageDatasetType;
  selectedDelay: string;
  selectedUniverse: string;
  selectedRegion: string;
}

function DatasetCard({ dataset, type, selectedUniverse, selectedRegion, selectedDelay }: DatasetCartProps) {
  const history = useDataExplorerHistory();
  const trackEvent = useDataExplorerTrackCustomEvent();
  const cardClickHandler = () => {
    const datasetId = dataset.id;
    if (!datasetId) {
      return;
    }

    const { region, delay, universe } = getExistingUniverse({
      existingRegions: dataset.regions as Region[],
      selectedRegion: selectedRegion,
      selectedDelay: selectedDelay,
      selectedUniverse: selectedUniverse,
    });

    history.push(
      getSingleDatasetPath({
        datasetId: datasetId,
        query: stringifyFilters({
          region,
          delay,
          universe,
        }),
      })
    );
    trackEvent(`de_click_${type}_dataset`);
  };

  return (
    <Card className="dataset-card" onClick={cardClickHandler}>
      <Card.Content>
        <h3 className="dataset-card__header">{dataset?.name ?? <Skeleton />}</h3>

        <Card.Description className="dataset-card__description">{dataset?.description ?? <Skeleton count={8} />}</Card.Description>
      </Card.Content>

      <Card.Content extra>
        {type === 'starter' && (
          <div>
            {dataset?.regions ? (
              <>
                <FormattedMessageHelper id={'dataExplorer.main.regions'} /> {dataset?.regions?.join(', ')}
              </>
            ) : (
              <Skeleton />
            )}
          </div>
        )}
        {type === 'theme' && <div>{dataset?.themes ? <ThemeLabel themes={dataset?.themes} /> : <Skeleton />}</div>}
      </Card.Content>
    </Card>
  );
}

export default withRouter(injectIntl(MainDataPage));
